import { AppContext, BookingData, Step } from "../../../AppDataProvider/AppDataProvider";
import { corners, marginBottom, marginLeft, marginRight, marginX, media, paddingX, paddingY, typography } from "../../styles";
import React, { useContext } from "react";
import { ServiceTier } from "@src/App/AppDataProvider/types";
import CustomerTranslations from "../../../utils/CustomerTranslations/";
import BookingInfo from "./BookingInfo/BookingInfo";
import CollapsibleSection from "../CollapsibleSection/CollapsibleSection";
import { Divider } from "../Divider/Divider";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getBookingPrices } from "./PriceInfo/getBookingPrices/getBookingPrices";
import { PriceInfo } from "./PriceInfo/PriceInfo";
import ProgressBar from "./ProgressBar/ProgressBar";
import styled from "styled-components";
import { useViewportSize } from "../../../utils";
export type ProgressTrackerProps = {
  className?: string;
};
const Container = styled.div`
  width: 100%;
  max-width: 20rem;
  justify-self: right;
  @media ${media.tablet} {
    ${marginBottom.l}
  }
  @media ${media.mobile} {
    ${marginBottom.m}
  }
`;
const Content = styled(CollapsibleSection)`
  ${paddingX.m}
  ${paddingY.s}

  border: 1px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  background: ${({
  theme
}) => theme.brand.cssVars["--d-card"]};
  ${corners.m}
  border-top: none;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
`;
const TierCaret = styled(FontAwesomeIcon).attrs({
  icon: faCaretRight
})`
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
  /*
  If the left and right margins match exactly, the spacing actually looks uneven because the icon
  is weighted more heavily to the left, so the right margin seems bigger than it actually is.
  By making the right margin slightly smaller, it visually appears much more evenly balanced.
  */
  ${marginLeft.s}
  ${marginRight.xs}
`;
const Tier = styled.div`
  display: inline;
  ${typography.small}
`;
const VerticalDivider = styled(Divider).attrs({
  vertical: true
})`
  ${marginX.m}
`;
type CalculateCompletionPercentageOpts = {
  bookingData: BookingData;
  currentStep: Step;
};
const calculateCompletionPercentage = (options: CalculateCompletionPercentageOpts): number => {
  const {
    bookingData,
    currentStep
  } = options;
  const resourceSelectionBeforeAvailability = bookingData.organisation?.resourceSelectionBeforeAvailability;
  switch (currentStep) {
    case "multiTier":
      return 25;
    case "pricePoints":
      return 30;
    case "resources":
      return resourceSelectionBeforeAvailability ? 40 : 50;
    case "availability":
      return resourceSelectionBeforeAvailability ? 50 : 40;
    case "recurringBooking":
      return 60;
    case "bookingQuestions":
      return 70;
    case "details":
      return 80;
    case "payments":
      return 90;
    case "bookingCreated":
      return 100;
    case "waitlist":
      return 80;
    case "waitlistConfirmation":
      return 100;
    default:
      return 10;
  }
};
export function ProgressTracker({
  className
}: ProgressTrackerProps) {
  const viewport = useViewportSize();
  const {
    appData
  } = useContext(AppContext);
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    bookingData,
    currentStep
  } = appData;
  const {
    organisation,
    service,
    tier
  } = bookingData;

  // Don't show progress tracker unless we have organisation and service,
  // and don't show on organisations, services or booking created steps
  if (!organisation || !service || ["organisations", "services", "bookingCreated"].includes(currentStep)) return null;
  const {
    showPricesOnBookingTools
  } = organisation;
  const bookingPrices = getBookingPrices(bookingData);
  const getHeaderContent = (selectedServiceName: string, selectedTier?: ServiceTier) => <>
      {selectedServiceName}
      {selectedTier ? <>
          <TierCaret />
          <Tier>{selectedTier.name}</Tier>
        </> : null}
    </>;
  const completionPercentage = calculateCompletionPercentage({
    bookingData,
    currentStep
  });
  const collapseDisabled = viewport.width >= media.thresholds.desktop;
  const {
    name
  } = translateEntity({
    document: service,
    type: "service"
  });
  return <>
      <Container {...{
      className
    }} data-sentry-element="Container" data-sentry-source-file="ProgressTracker.tsx">
        <ProgressBar progress={completionPercentage} data-sentry-element="ProgressBar" data-sentry-source-file="ProgressTracker.tsx" />
        <Content headerContent={getHeaderContent(name, tier)} small collapseDisabled={collapseDisabled} title={name} {...viewport.width >= media.thresholds.tablet && {
        open: true
      }} data-sentry-element="Content" data-sentry-source-file="ProgressTracker.tsx">
          <BookingInfo data-sentry-element="BookingInfo" data-sentry-source-file="ProgressTracker.tsx" />
          {showPricesOnBookingTools && bookingPrices.length > 0 && bookingData.attendees && <>
                <Divider />
                <PriceInfo {...{
            bookingPrices
          }} />
              </>}
        </Content>
      </Container>
      {viewport.width >= media.thresholds.desktop && <VerticalDivider />}
    </>;
}
export default ProgressTracker;